<script lang="ts" setup generic="T">
	import type { PolicyResponse, TableFilterOptions, TableColumn } from '@/types'
	import type { filterCollectionSchema } from '~/components/Forms/filter.vue'
	import usePurchases from '~/services/apis/purchases-info'

	const props = defineProps<{
		planDetails?: PlanInformationContext | null
		planCaption: string
		planName: string
		tableRecordColumn: TableColumn[]
		categoryType: ProductCategoryType
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		fetchPendingPolicies: (...rest: any) => Promise<PaginationContext<PolicyResponse[]>>
	}>()

	const emits = defineEmits<{
		(event: 'change', param: (string | number)[]): void
	}>()
	const { plan_id } = useRoute().params
	const { getPolicies } = usePurchases()

	const filterBy = shallowRef('active')
	const searchValue = shallowRef('')
	const confirmDialog = shallowRef(false)
	const isUpdating = shallowRef(false)
	const loadingRemovalSummary = shallowRef(false)
	const selectedRecordIds = ref<string[]>([])
	const filterCollection: filterCollectionSchema[] = [
		{
			key: 'active',
			value: 'Active'
		},
		{
			key: 'pending',
			value: 'Pending'
		}
	]

	const hasSelectedRecords = computed(() => selectedRecordIds.value.length > 0)

	// By Default it should filter pending removal policies or removed policies
	const filterRemovedPolicies = (list: PolicyResponse[]) => {
		if (list && list.length) {
			return list.filter(item => !item.to_be_removed && !item.is_terminated)
		}
		return list
	}

	const tableOptions = computed<TableFilterOptions>(() => ({
		multipleSelection: true,
		networkKey: 'policy-list',
		filter: { search: searchValue.value },
		watch: [filterBy.value, isUpdating.value],
		transformResponse: e => filterRemovedPolicies(e as PolicyResponse[])
	}))

	const resetTableSelection = () => {
		selectedRecordIds.value = []
	}
	const getRecord = async (query: object) => {
		resetTableSelection()
		if (filterBy.value !== 'pending') {
			const result = await getPolicies({ purchase_id: String(plan_id), ...(query || {}) })
			return result ?? null
		} else {
			return await props.fetchPendingPolicies({}, String(plan_id), query)
		}
	}
	const policyTable = useTemplateRef('policy-table')
	const refreshTable = () => policyTable.value?.refreshPolicies()

	watch(selectedRecordIds, item => emits('change', item))
</script>

<template>
	<div>
		<div class="mb-8 flex h-[48px] w-full items-center rounded-md border px-4">
			<forms-search-field v-model:value="searchValue" class="flex-1" :use-custom-class="false" />

			<div class="mt-1.5 h-[29px]">
				<forms-filter
					v-model:filter-by="filterBy"
					class="mx-3 max-w-sm flex-none border-l pl-5 tracking-wide"
					:filter-collection="filterCollection"
					:use-default-class="false"
				/>
			</div>
		</div>

		<tables-policies
			ref="policy-table"
			v-model:selected="selectedRecordIds"
			unique-key="id"
			:columns="tableRecordColumn"
			:records="getRecord"
			:table-config="tableOptions"
		>
			<template #details="{ record }">
				<nuxt-link
					type="button"
					aria-label="activate staff"
					class="text-sm font-medium text-primary-2"
					:to="`/mycover/custodian/${record.id}?status=${filterBy}`"
				>
					<span class=""> View </span>
				</nuxt-link>
			</template>
		</tables-policies>

		<ui-button
			v-if="hasSelectedRecords"
			:loading="loadingRemovalSummary"
			:disabled="loadingRemovalSummary"
			class="fixed bottom-8 left-0 right-0 mx-5 max-w-md justify-center !border-error-500 !bg-error-500 !text-white transition sm:mx-auto"
			@click="confirmDialog = true"
		>
			Deactivate {{ selectedRecordIds.length }} polic{{ selectedRecordIds.length > 1 ? 'ies' : 'y' }}
		</ui-button>

		<employees-deactivate-policy-modal
			v-model="confirmDialog"
			v-model:selected-ids="selectedRecordIds"
			:product-category="planDetails?.product?.productCategory?.name ?? ''"
			:product-code="planDetails?.product?.code_name ?? 0"
			:status="filterBy"
			with-policy-id
			@submitted="() => refreshTable()"
		/>
	</div>
</template>
